import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CustomPopover from '@components/CustomPopover'
import { CssTruncation } from '@components/TruncateLines/index.styles'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { preparePendoSavedSearchEvent, preparePendoSearchEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoEventType, PendoSearchEventType } from '@modules/pendo/types'
import SavedSearchesOverview from '@modules/savedSearch/components/SavedSearchesOverview'
import { SavedSearch } from '@modules/savedSearch/types'
import searchApi from '@modules/search/api'
import { ProfileType } from '@modules/search/types'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useQueryParams } from '@shared/hooks/useQueryParams'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import { useQuery } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import { ProfileTypeFilters } from './ProfileTypeFilters'
import SearchAutocomplete from './SearchAutocomplete'
import { SearchButtons } from './SearchButtons'
import { SearchIconWrapper, SearchInputWrapper, SearchSectionWrapper, SearchStartRowContainer } from './index.styles'

export interface SearchSectionProps {
  searchString: string
  handleSearchClick: (searchStr: string) => void
  handleResetAll: () => void
  handleSetSearchableProfiles: (profileType: ProfileType, enabled: boolean) => void
  handleSetAllSearchableProfiles: (value: boolean) => void
  isAllProfilesEnabled: boolean
  searchableProfilesMap: Record<string, boolean>
  selectedFiltersByProfiles?: Nullable<{ [key: string]: unknown[] }>
  savedSearches?: SavedSearch[]
  setSavedSearch?: (savedSearch: Nullable<SavedSearch>) => void
  appliedSavedSearchName?: string
  toggleFiltersDrawer?: () => void
}

const SearchSection = (props: SearchSectionProps) => {
  const {
    searchString,
    handleSearchClick,
    handleResetAll,
    handleSetSearchableProfiles,
    handleSetAllSearchableProfiles,
    isAllProfilesEnabled,
    searchableProfilesMap,
    selectedFiltersByProfiles,
    savedSearches,
    setSavedSearch,
    appliedSavedSearchName,
    toggleFiltersDrawer,
  } = props

  const [disablePredictiveSearch, setDisablePredictiveSearch] = useState(false)
  const [localSearchStr, setLocalSearchStr] = useState(searchString || '')
  const debouncedValue = useDebouncedValue(localSearchStr, 300)
  const { user } = useAuthContext()
  const { trackPendoEvent } = usePendoTrackEvent()
  const { t } = useTranslation()
  const { isDesktop } = useScreenDimensions()

  const [savedSearchesOverviewAnchor, setSavedSearchesOverviewAnchor] = useState<Nullable<HTMLElement>>(null)

  const savedSearchButtonRef = useRef<HTMLButtonElement | null>(null)

  const { hasAccessToPredictiveSearch, hasAccessToSavedSearch } = useFeatureFlags()

  const isSavedSearchButtonEnabled = !!savedSearches?.length

  const { get, clearOneSilently } = useQueryParams()
  const savedSearchOpenQsKey = 'saved-search-open'
  const savedSearchOpenQsValue = get(savedSearchOpenQsKey)

  useEffect(() => {
    if (savedSearchOpenQsValue && isSavedSearchButtonEnabled && savedSearchButtonRef.current) {
      clearOneSilently(savedSearchOpenQsKey)
      setSavedSearchesOverviewAnchor(savedSearchButtonRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearchOpenQsValue, isSavedSearchButtonEnabled])

  const { data: { terms, companies } = {} } = useQuery({
    queryKey: [searchApi.getPredictiveSearch.key, debouncedValue],
    queryFn: () => searchApi.getPredictiveSearch.fn(debouncedValue),
    enabled: debouncedValue?.length > 1 && !disablePredictiveSearch && hasAccessToPredictiveSearch,
  })

  const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDisablePredictiveSearch(false)
    setLocalSearchStr(e.target?.value || '')
  }, [])

  const onSelectPredictiveSearch = useCallback((value: string) => {
    setDisablePredictiveSearch(true)

    if (value) {
      setLocalSearchStr(value)
      onSearchClick(value?.includes(' ') ? `"${value}"` : value)

      trackPendoEvent(
        PendoSearchEventType.PredictiveSearch,
        preparePendoSearchEvent({ eventType: PendoSearchEventType.PredictiveSearch, user }),
      )
      return
    }

    onSearchClick()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearchClick = useCallback((value?: string) => handleSearchClick(value ?? localSearchStr), [localSearchStr])

  const onResetSearchString = useCallback(() => {
    setLocalSearchStr('')
    trackPendoEvent(
      PendoSearchEventType.ClearSearchIcon,
      preparePendoSearchEvent({ eventType: PendoSearchEventType.ClearSearchIcon, user }),
    )
  }, [setLocalSearchStr, user, trackPendoEvent])

  const onResetAll = useCallback(() => {
    handleResetAll()
    onResetSearchString()
  }, [handleResetAll, onResetSearchString])

  const resetSearchName = () => {
    setSavedSearch?.(null)
  }

  useEffect(() => {
    if (searchString === localSearchStr) {
      return
    }

    setLocalSearchStr(searchString)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString])

  const onKeyPress = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return
    }

    trackPendoEvent(
      PendoSearchEventType.SearchEnterTrigger,
      preparePendoSearchEvent({ eventType: PendoSearchEventType.SearchEnterTrigger, user }),
    )

    onSearchClick()
  }
  const newSavedSearchesCount =
    savedSearches?.reduce((acc, search) => {
      return (search.newItemsCount ?? 0) > 0 ? acc + 1 : acc
    }, 0) ?? 0
  const badgeContent = newSavedSearchesCount > 99 ? '99+' : newSavedSearchesCount.toString()

  const handleSavedSearchesOverviewClick = (e: React.MouseEvent<HTMLElement>) => {
    setSavedSearchesOverviewAnchor(e.currentTarget as HTMLDivElement)

    const pendoEvent = preparePendoSavedSearchEvent({
      anchorEl: e.currentTarget,
      savedSearchId: null,
      user,
      eventType: 'overview',
    })

    trackPendoEvent(PendoEventType.SavedSearchPopoverOpened, pendoEvent)
  }
  const savedSearchesBtnTitle = appliedSavedSearchName ? (
    <CssTruncation>{appliedSavedSearchName}</CssTruncation>
  ) : (
    t(translationKeys.SAVED_SEARCHES)
  )

  return (
    <SearchSectionWrapper>
      <SearchStartRowContainer>
        <SearchInputWrapper>
          <Box width="100%" id="search-keyword_box">
            <SearchAutocomplete
              localSearchStr={localSearchStr}
              terms={terms}
              companies={companies}
              onKeyPress={onKeyPress}
              onSearch={onSearch}
              onSelectPredictiveSearch={onSelectPredictiveSearch}
              onClear={onResetSearchString}
            />
          </Box>
          <SearchIconWrapper
            id="search-keyword_button"
            onClick={() => {
              onSearchClick()
              trackPendoEvent(
                PendoSearchEventType.SearchIconTrigger,
                preparePendoSearchEvent({ eventType: PendoSearchEventType.SearchIconTrigger, user }),
              )
            }}
            data-testid="search_icon"
          >
            <SearchIcon htmlColor={colors.white} fontSize="inherit" />
          </SearchIconWrapper>
        </SearchInputWrapper>
        {isDesktop && (
          <SearchButtons
            ref={savedSearchButtonRef}
            toggleFiltersDrawer={toggleFiltersDrawer}
            onResetAll={onResetAll}
            hasAccessToSavedSearch={hasAccessToSavedSearch}
            isSavedSearchButtonEnabled={isSavedSearchButtonEnabled}
            appliedSavedSearchName={appliedSavedSearchName}
            handleSavedSearchesOverviewClick={handleSavedSearchesOverviewClick}
            savedSearchesBtnTitle={savedSearchesBtnTitle}
            newSavedSearchesCount={newSavedSearchesCount}
            badgeContent={badgeContent}
          />
        )}
      </SearchStartRowContainer>

      {isDesktop && (
        <ProfileTypeFilters
          handleSetSearchableProfiles={handleSetSearchableProfiles}
          handleSetAllSearchableProfiles={handleSetAllSearchableProfiles}
          isAllProfilesEnabled={isAllProfilesEnabled}
          searchableProfilesMap={searchableProfilesMap}
          selectedFiltersByProfiles={selectedFiltersByProfiles}
        />
      )}

      {!isDesktop && (
        <SearchButtons
          ref={savedSearchButtonRef}
          toggleFiltersDrawer={toggleFiltersDrawer}
          onResetAll={onResetAll}
          hasAccessToSavedSearch={hasAccessToSavedSearch}
          isSavedSearchButtonEnabled={isSavedSearchButtonEnabled}
          appliedSavedSearchName={appliedSavedSearchName}
          handleSavedSearchesOverviewClick={handleSavedSearchesOverviewClick}
          savedSearchesBtnTitle={savedSearchesBtnTitle}
          newSavedSearchesCount={newSavedSearchesCount}
          badgeContent={badgeContent}
        />
      )}
      {savedSearchesOverviewAnchor && (
        <CustomPopover
          sx={{ marginTop: '10px' }}
          id="saved-searches-overview"
          anchor={savedSearchesOverviewAnchor}
          setAnchor={setSavedSearchesOverviewAnchor}
        >
          <SavedSearchesOverview
            appliedSavedSearchName={appliedSavedSearchName}
            resetSearchName={resetSearchName}
            onSearchSelected={setSavedSearch}
            close={() => setSavedSearchesOverviewAnchor(null)}
          />
        </CustomPopover>
      )}
    </SearchSectionWrapper>
  )
}

export default memo(SearchSection)
